import type { UnifiedAgentRow, AgentSource } from "./unified-agents";
// Static imports (no cycle: neither store imports agent-actions). The synchronous
// helpers below previously used CommonJS require(), which is undefined in the Vite
// ESM browser bundle and threw at runtime.
import { useLocalTerminalStore } from "@/stores/localTerminalStore";
import { useChatTabStore } from "@/stores/chatTabStore";

export type AgentAction = "focus" | "stop" | "resume" | "open_folder" | "delete";

type ActionHandler = (row: UnifiedAgentRow) => Promise<void>;

let _navigate: ((to: string) => void) | null = null;

export function setAgentActionNavigator(navigate: (to: string) => void) {
  _navigate = navigate;
}

function navigateTo(path: string) {
  if (_navigate) _navigate(path);
}

function findTerminalScope(tabId: string): string | null {
  const scopes = useLocalTerminalStore.getState().scopes;
  for (const [scopeKey, scope] of Object.entries(scopes)) {
    if (scope.tabs.some((t) => t.id === tabId)) {
      return scopeKey;
    }
  }
  return null;
}

function findChatTabSessionId(tabId: string): string | null {
  const tabs = useChatTabStore.getState().tabs;
  for (const projectTabs of Object.values(tabs)) {
    const tab = projectTabs.find((t) => t.id === tabId);
    if (tab?.sessionId) return tab.sessionId;
  }
  return null;
}

const chatActions: Partial<Record<AgentAction, ActionHandler>> = {
  async focus(row) {
    const { useChatTabStore } = await import("@/stores/chatTabStore");
    const { useSidebarStore } = await import("@/stores/sidebarStore");
    if (row.projectId != null) {
      useChatTabStore.getState().setActiveTab(row.projectId, row.nativeId);
      useSidebarStore.getState().setSelectedProjectSubTab("chat");
      navigateTo(`/projects/${row.projectId}`);
    }
  },

  async stop(row) {
    const { useAiChatStore } = await import("@/stores/aiChatStore");
    const sessionId = findChatTabSessionId(row.nativeId);
    if (sessionId) {
      await useAiChatStore.getState().stopSession(sessionId);
    }
  },

  async resume(_row) {
    // not yet implemented
  },

  async delete(row) {
    const { useChatTabStore } = await import("@/stores/chatTabStore");
    if (row.projectId != null) {
      useChatTabStore.getState().closeTab(row.projectId, row.nativeId);
    }
  },
};

const terminalActions: Partial<Record<AgentAction, ActionHandler>> = {
  async focus(row) {
    const { useLocalTerminalStore } = await import("@/stores/localTerminalStore");
    const { useSidebarStore } = await import("@/stores/sidebarStore");
    const scopeKey = findTerminalScope(row.nativeId);
    if (scopeKey) {
      useLocalTerminalStore.getState().setActiveTab(scopeKey, row.nativeId);
    }
    if (row.projectId != null) {
      useSidebarStore.getState().setSelectedProjectSubTab("terminal");
      navigateTo(`/projects/${row.projectId}`);
    }
  },

  async stop(row) {
    const { useLocalTerminalStore } = await import("@/stores/localTerminalStore");
    const scopeKey = findTerminalScope(row.nativeId);
    if (scopeKey) {
      await useLocalTerminalStore.getState().closeTab(scopeKey, row.nativeId);
    }
  },

  async open_folder(row) {
    if (!row.cwd) return;
    try {
      const tauri = (window as any).__TAURI__;
      if (tauri?.shell?.open) {
        await tauri.shell.open(row.cwd);
      } else {
        console.log("Open folder: Tauri shell not available. Path:", row.cwd);
      }
    } catch (err) {
      console.error("Failed to open folder:", err);
    }
  },

  async delete(row) {
    const scopeKey = findTerminalScope(row.nativeId);
    if (!scopeKey) return;
    const { useLocalTerminalStore } = await import("@/stores/localTerminalStore");
    await useLocalTerminalStore.getState().closeTab(scopeKey, row.nativeId);
  },
};

const workflowActions: Partial<Record<AgentAction, ActionHandler>> = {
  async focus(row) {
    const { useReviewStore } = await import("@/stores/reviewStore");
    useReviewStore.getState().selectRun(row.nativeId);
  },

  async stop(row) {
    const { useReviewStore } = await import("@/stores/reviewStore");
    await useReviewStore.getState().stopRun(row.nativeId);
  },

  async resume(row) {
    const { useReviewStore } = await import("@/stores/reviewStore");
    await useReviewStore.getState().retryRun(row.nativeId);
  },

  async delete(row) {
    const { useReviewStore } = await import("@/stores/reviewStore");
    await useReviewStore.getState().deleteRun(row.nativeId);
  },
};

export const ACTION_HANDLERS: Record<
  AgentSource,
  Partial<Record<AgentAction, ActionHandler>>
> = {
  chat: chatActions,
  terminal: terminalActions,
  workflow: workflowActions,
};
